import React, { useRef, useEffect, useMemo } from 'react';
import { Ico, COLORS, COLOR_HEX } from './icons';

export function TabEditPopover({ tab, rect, loc, onClose, onUpdate, onDelete }) {
  const popRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [tab.id]);

  // Click-outside / Escape to dismiss
  useEffect(() => {
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // loc is the trigger's screen edge, so the popover opens on the opposite side
  const style = useMemo(() => {
    const POP_W = 240;
    const POP_H = 200;
    const PAD = 8;
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const base = { position:'fixed', width: POP_W, zIndex: 200 };
    if (loc === 'left') {
      let left = rect.right + PAD;
      let top = rect.top;
      left = Math.max(MARGIN, Math.min(left, vw - POP_W - MARGIN));
      top = Math.max(MARGIN, Math.min(top, vh - POP_H - MARGIN));
      return { ...base, top, left };
    }
    if (loc === 'right') {
      let right = vw - rect.left + PAD;
      let top = rect.top;
      right = Math.max(MARGIN, Math.min(right, vw - POP_W - MARGIN));
      top = Math.max(MARGIN, Math.min(top, vh - POP_H - MARGIN));
      return { ...base, top, right };
    }
    if (loc === 'top') {
      let left = rect.left;
      let top = rect.bottom + PAD;
      left = Math.max(MARGIN, Math.min(left, vw - POP_W - MARGIN));
      top = Math.max(MARGIN, Math.min(top, vh - POP_H - MARGIN));
      return { ...base, top, left };
    }
    let left = rect.left;
    let bottom = vh - rect.top + PAD;
    left = Math.max(MARGIN, Math.min(left, vw - POP_W - MARGIN));
    bottom = Math.max(MARGIN, Math.min(bottom, vh - POP_H - MARGIN));
    return { ...base, bottom, left };
  }, [rect, loc]);

  return (
    <div ref={popRef} className="tab-edit-pop" style={style} onClick={(e)=>e.stopPropagation()}>
      <div className="tep-hd">
        <div className={`tep-dot ${!tab.color ? 'tep-dot-empty' : ''}`}
             style={{ background: tab.color ? COLOR_HEX[tab.color] : 'transparent' }}/>
        <input
          ref={inputRef}
          className="tep-input"
          value={tab.label}
          onChange={(e)=>onUpdate({ label: e.target.value })}
          onKeyDown={(e)=>{ if (e.key==='Enter') onClose(); }}
          placeholder="Tab name"/>
        <button className="tep-icon" onClick={onClose} title="Done">
          <Ico.Close width="14" height="14"/>
        </button>
      </div>
      <div className="tep-section">
        <div className="tep-lbl">Color</div>
        <div className="tep-swatches">
          <button className={`tep-swatch tep-swatch-none ${!tab.color?'on':''}`}
                  onClick={()=>onUpdate({ color: null })}
                  title="No color"/>
          {COLORS.map(c => (
            <button key={c}
                    className={`tep-swatch ${tab.color===c?'on':''}`}
                    style={{ background: COLOR_HEX[c] }}
                    onClick={()=>onUpdate({ color: c })}
                    title={c}/>
          ))}
        </div>
      </div>
      <div className="tep-foot">
        <button className="tep-del" onClick={onDelete}>
          <Ico.Trash width="12" height="12"/> Delete tab
        </button>
      </div>
    </div>
  );
}
